import { isEmpty, verifyInterface, ProcessQueue } from "../../../utils";
import { Modal } from "bootstrap";
import { Sound } from "../../../sound";
import { AssignScore, getGlobalHealthySocket, terminalRequest } from "../utils";
import * as Turbo from "@hotwired/turbo";
import {
  AwayHandler,
  REQUIRED_MP3_VALUES,
  TriggeredAction,
} from "../awayHandler";

export class Scan {
  requiredInterface = [
    // targets
    "tilesTarget",
    "addTileTarget",
    "addTileTemplateTarget",
    "assignedAssetsInputTarget",
    "employeeIdInputTarget",
    "emptyTileTemplateTarget",
    "goingAwayModalTarget",
    "skeletonTileTemplateTarget",
    // values
    "assetTileUrlValue",
    "assignSuccessUrlValue",
    "assignStoreUrlValue",
    "newAssetScannedMp3Value",
    "pickEmployeeUrlValue",
  ].concat(REQUIRED_MP3_VALUES);

  constructor(controllerCtxt) {
    this.ctrl = controllerCtxt;
    this.requestRescan();
    verifyInterface(this.ctrl, {
      requiredInterface: this.requiredInterface,
      checkCSRF: true,
    });
    this.verifyAndCopyTemplates();
    this.handledDetects = {};
    this.warningsQueue = new ProcessQueue(
      this.waitForUserToAcceptWarning.bind(this),
    );
    this.warningsQueueLock = undefined;
    this.sound = new Sound({
      cancel: this.ctrl.cancelMp3Value,
      newAsset: this.ctrl.newAssetScannedMp3Value,
      error: this.ctrl.errorMp3Value,
      toEmployee: this.ctrl.toEmployeeMp3Value,
      toStore: this.ctrl.toStoreMp3Value,
    });

    this.awayHandler = new AwayHandler({
      actionToTrigger: this.actionToTrigger.bind(this),
      controllerCtxt: this.ctrl,
      sound: this.sound,
    });
    this.awayHandler.registerNavigateAwayWarn();

    this.refreshAssetDataInDOM([]);
  }

  deConstructor() {
    this.awayHandler.unregisterNavigateAwayWarn();
    window.terminal.handledDetects = this.handledDetects;
  }

  verifyAndCopyTemplates() {
    ["empty", "skeleton", "add"].forEach((name) => {
      const children = this.ctrl[`${name}TileTemplateTarget`].children;
      if (children.length !== 1) {
        throw new Error(
          `DEVELOPMENT ERROR. Expected exactly one element in ${name} tile template.`,
        );
      }
      // clone deep
      this[`_${name}TileNode`] = children[0].cloneNode(true);
    });
  }

  handleEvent(event) {
    switch (event.action) {
      case "assetsDetected":
        this.handleDetectedAssets(event.detects);
        break;
      case "employeeDetected":
        this.handleEmployeeDetected(event.id);
        break;
      default:
        console.error("unsupported event for mode 'Scan'", event);
    }
  }

  handleDetectedAssets(detects) {
    if (isEmpty(detects)) {
      console.warn("dropping empty detects.");
      return;
    }

    const knownAssignedIds = this.getAssigned().map(([i, _]) => i);
    const knownAssetIds = Object.keys(this.handledDetects);
    const newDetects = Object.fromEntries(
      Object.entries(detects).filter(
        ([assetId]) => !knownAssetIds.includes(assetId),
      ),
    );

    Object.entries(this.handledDetects).forEach(([assetId, assignedScore]) => {
      if (!AssignScore.isDetectedUnassigned(assignedScore)) return;
      if (!Object.keys(detects).includes(assetId)) return;
      this.handledDetects[assetId] = detects[assetId];
    });

    this.handledDetects = {
      ...this.handledDetects,
      ...newDetects,
    };

    const newlyAssignedIds = this.getAssigned()
      .map(([newId, _]) => newId)
      .filter((newId) => !knownAssignedIds.includes(newId));

    this.refreshAssetDataInDOM(newlyAssignedIds);
  }

  getAssigned() {
    return Object.entries(this.handledDetects).filter(([_, s]) =>
      AssignScore.isAssigned(s),
    );
  }

  assignManually(event) {
    const assetId = event.params.assetId;
    this.handledDetects[assetId] = AssignScore.ManuallyAssigned;
    event.target.classList.add("disabled");
    event.target.innerHTML = "<i class='bi bi-check-circle-fill'></i>";
    this.refreshAssetDataInDOM([assetId]);
  }

  unassignManually(event) {
    const assetId = event.params.assetId;
    this.sound.playOnce("cancel").then();
    event.target.closest("div.asset-tile").remove();
    this.handledDetects[assetId] = AssignScore.ManuallyUnassigned;
    this.refreshAssetDataInDOM([]);
  }

  refreshAssetDataInDOM(newlyAssignedIds) {
    if (newlyAssignedIds.length !== 0) {
      this.closeAllModals({ keep_stream_ids: ["asset_warning"] });
    }
    this.swipeEmptyTiles();
    this.addAssetSkeletons(newlyAssignedIds);
    this.requestAssets(newlyAssignedIds).catch(console.error);
    this.populateEmptyTiles();
    this.updateAddDisplay();
    this.setAssignedAssetsInputs();
  }

  async requestAssets(newlyAssignedIds) {
    for (const assetId of newlyAssignedIds) {
      const formData = new FormData();
      formData.append("asset_id", assetId);
      const turboResponse = await terminalRequest(this.ctrl.assetTileUrlValue, {
        formData,
      });
      if (!turboResponse.is_stream) {
        console.error("Expected stream, got", turboResponse);
        continue;
      }
      const streamMessage = turboResponse.html;
      const has_warning = !!turboResponse.response.headers.get(
        "X-ASSET-HAS-WARNING",
      );
      if (!has_warning) {
        Turbo.renderStreamMessage(streamMessage);
      } else {
        this.warningsQueue.processNext([streamMessage]);
      }
    }
  }

  async waitForUserToAcceptWarning(stream) {
    Turbo.renderStreamMessage(stream);
    await this.waitForWarningModal({ toBeShown: true });
    await this.waitForWarningModal({ toBeShown: false });
  }

  async waitForWarningModal({ toBeShown }) {
    const warningModalEl = document.querySelector(
      '[data-modal-stream-id-value="asset_warning"]',
    );
    const isShown = warningModalEl.classList.contains("show");
    if (isShown === toBeShown) return;
    const eventType = toBeShown ? "shown.bs.modal" : "hidden.bs.modal";
    return new Promise((resolve) => {
      warningModalEl.addEventListener(eventType, resolve);
    });
  }

  swipeEmptyTiles() {
    const emptyTiles = this.ctrl.tilesTarget.querySelectorAll("div.empty-tile");
    for (const t of emptyTiles) {
      t.remove();
    }
  }

  populateEmptyTiles() {
    const tilesTarget = this.ctrl.tilesTarget;
    const createCount = 6 - tilesTarget.childElementCount;
    if (createCount <= 0) {
      return;
    }
    tilesTarget.appendChild(this._addTileNode);
    for (let i = createCount - 1; i > 0; i -= 1) {
      const emptyNode = this._emptyTileNode.cloneNode(true);
      tilesTarget.appendChild(emptyNode);
    }
  }

  addAssetSkeletons(assetIdList) {
    if (assetIdList.length === 0) return;
    this.sound.playOnce("newAsset").then();

    for (const assetId of assetIdList) {
      const assetSkeleton = this._skeletonTileNode.cloneNode(true);
      assetSkeleton.id = `asset_tile_${assetId}`;
      this.ctrl.tilesTarget.appendChild(assetSkeleton);
    }
  }

  setAssignedAssetsInputs() {
    const idList = Object.entries(this.handledDetects)
      .filter(([_, score]) => AssignScore.isAssigned(score))
      .map(([assetId, _]) => assetId);
    const disabled = idList.length === 0;
    const idListStr = idList.join(",");

    for (const idsInput of this.ctrl.assignedAssetsInputTargets) {
      idsInput.value = idListStr;
      const submit = idsInput.form.querySelector("[type=submit]");
      if (submit) {
        submit.disabled = disabled;
      }
    }
  }

  updateAddDisplay() {
    const idList = Object.entries(this.handledDetects)
      .filter(([_, score]) => AssignScore.isUnassigned(score))
      .map(([assetId, _]) => assetId);
    const nothingToAdd = idList.length === 0;

    const url = new URL(this.ctrl.addTileTarget.href);
    url.searchParams.set("id_list", idList.join(","));
    this.ctrl.addTileTargets.forEach((addTile) => {
      addTile.href = url.href;
      if (nothingToAdd) {
        addTile.classList.add("d-none");
      } else {
        addTile.classList.remove("d-none");
      }
    });

    if (nothingToAdd) {
      this.closeAllModals({ keep_stream_ids: ["asset_warning"] });
    }
  }

  closeAllModals(options = { keep_stream_ids: [] }) {
    document
      .querySelectorAll('[data-controller="modal"]')
      .forEach((modalEl) => {
        if (!modalEl.classList.contains("show")) return;
        const streamIdAttr = modalEl.attributes["data-modal-stream-id-value"];
        if (
          streamIdAttr &&
          options.keep_stream_ids.includes(streamIdAttr.value)
        ) {
          return;
        }
        const modalInstance = Modal.getInstance(modalEl);
        if (modalInstance === null) return;
        modalInstance.hide();
      });
  }

  handleEmployeeDetected(employeeId) {
    const idInput = this.ctrl.employeeIdInputTarget;
    idInput.value = employeeId;
    idInput.form.requestSubmit();
  }

  async requestRescan() {
    const socket = await getGlobalHealthySocket();
    socket.send(JSON.stringify({ action: "requestRescan" }));
  }

  actionToTrigger(turboBeforeVisitEvent) {
    if (this.getAssigned().length <= 0) {
      return TriggeredAction.None;
    }
    if (turboBeforeVisitEvent) {
      const targetUrl = new URL(turboBeforeVisitEvent.detail.url);
      if (targetUrl.pathname === this.ctrl.assignSuccessUrlValue) {
        const queryKeys = [...targetUrl.searchParams.keys()];
        if (queryKeys.includes("employee_id")) {
          return TriggeredAction.ToEmployee;
        }
        return TriggeredAction.ToStore;
      }
      if (targetUrl.pathname === this.ctrl.assignStoreUrlValue) {
        return TriggeredAction.None;
      }
      if (targetUrl.pathname === this.ctrl.pickEmployeeUrlValue) {
        if (targetUrl.searchParams.get("asset_id_list")) {
          return TriggeredAction.None;
        } else {
          // if the parameter is empty, the user tries to log in without assignment
          return TriggeredAction.Cancel;
        }
      }
    }
    return TriggeredAction.Cancel;
  }
}
